import { useEffect, useRef, useState } from 'react';

import useClientState from '@/state/useClientState';

interface BoundingBox {
  width: number;
  height: number;
}
const initialValue: BoundingBox = { width: 0, height: 0 };

type BoundingBoxCallback = (bb: BoundingBox) => void;

type RefCallback = (node: HTMLElement | null) => void;

/**
 * Gebruikt een ResizeObserver om altijd de juiste afmetingen te weten van bijv. een MWD op het scherm.
 * Door het responsive karakter van dit soort elementen zijn de afmetingen in pixels niet statisch,
 * terwijl het soms wel nodig is om te weten hoe groot een element is.
 * Bijvoorbeeld om te kunnen bepalen hoeveel 'tijd' er op het scherm wordt getoond om zo naar een element
 * te kunnen scrollen, etc.
 *
 * ```ts
 * const [width, height] = useResizeObserver(myRef, (bb) => {
 * });
 * ```
 */
function useResizeObserver(bbCallback: BoundingBoxCallback): RefCallback {
  const [ref, setRef] = useState<HTMLElement | null>(null);
  const boundingBoxRef = useRef<BoundingBox>(initialValue);

  /** Placed here proceduraly in order to prevent this component from being used/loaded more than once */
  const setMwdHeight = useClientState((state) => state.setMwdHeight);

  useEffect(() => {
    let didUnsubscribe = false;

    function callback(entries: ResizeObserverEntry[], __observer: ResizeObserver) {
      if (didUnsubscribe) return;
      const newBBox = rounded(observedBoundedBox(entries));
      if (newBBox.width !== boundingBoxRef.current.width || newBBox?.height !== boundingBoxRef.current.height) {
        bbCallback.apply(null, [newBBox]);
      }
      if (newBBox?.height && newBBox?.height !== boundingBoxRef.current.height) setMwdHeight(newBBox.height);
    }

    if (!ref) {
      console.log('WARNING: ref.current is falsy in useResizeObserver');
      return;
    }
    const resizeObserver = new ResizeObserver(callback);
    resizeObserver.observe(ref);

    return () => {
      didUnsubscribe = true;
      resizeObserver.disconnect();
    };
  }, [ref, bbCallback]);

  return setRef;
}

// Standalone functions

function observedBoundedBox(entries: ResizeObserverEntry[]): BoundingBox {
  if (!Array.isArray(entries) || entries.length < 1) {
    return { width: 0, height: 0 };
  }
  const borderBoxSize = entries[0].borderBoxSize[0];
  return { width: borderBoxSize.inlineSize, height: borderBoxSize.blockSize };
}

const rounded = (bb: BoundingBox): BoundingBox => ({
  width: Math.round(bb.width),
  height: Math.round(bb.height),
});
export { useResizeObserver };
export type { BoundingBoxCallback };
